import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import VehicleCard from '../components/VehicleCard';
import BackButton from '../components/BackButton';
import { WHATSAPP_LINKS } from '../constants/connect';
import usePageMeta from '../hooks/usePageMeta';
import './Pages.css';

const normalize = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const vehicleIsOwnStock = (vehicle) => {
  if (!vehicle || typeof vehicle !== 'object') return false;

  const type = normalize(vehicle.inventoryType);
  if (type) {
    return type === 'estoque';
  }

  if (typeof vehicle.ownStock === 'boolean') return vehicle.ownStock;
  if (typeof vehicle.isOwnStock === 'boolean') return vehicle.isOwnStock;

  const candidates = [
    vehicle.stockType,
    vehicle.inventorySource,
    vehicle.source,
    vehicle.group,
    vehicle.collection,
    vehicle.bucket,
    vehicle.tag,
    vehicle.category,
  ].filter(Boolean);

  if (candidates.some((value) => {
    const normalized = normalize(value);
    return (
      normalized === 'proprio' ||
      normalized === 'estoque proprio' ||
      normalized === 'showroom' ||
      normalized === 'connect'
    );
  })) {
    return true;
  }

  const listCandidates = [vehicle.tags, vehicle.labels, vehicle.flags].filter(Array.isArray);
  return listCandidates.some((list) =>
    list.some((value) => {
      const normalized = normalize(value);
      return (
        normalized.includes('proprio') ||
        normalized.includes('showroom') ||
        normalized.includes('connect')
      );
    })
  );
};

export default function Estoque() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: 'Estoque Connect - carros prontos para ver agora | Osasco',
    description:
      'Carros proprios selecionados pela Connect Veiculos. Agende sua visita e conheca os modelos disponiveis na garagem em Osasco.',
  });

  useEffect(() => {
    let mounted = true;
    api
      .get('/vehicles')
      .then((res) => {
        if (mounted) {
          setVehicles(Array.isArray(res.data) ? res.data : []);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const activeVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicle.active),
    [vehicles]
  );

  const ownStockVehicles = useMemo(
    () => activeVehicles.filter(vehicleIsOwnStock),
    [activeVehicles]
  );

  const visibleVehicles = ownStockVehicles.length > 0 ? ownStockVehicles : activeVehicles;
  const usingFallback = ownStockVehicles.length === 0;

  return (
    <div className="page-shell estoque-page">
      <BackButton />

      <section className="page-hero" aria-labelledby="estoque-title">
        <div className="page-hero__content">
          <h1 id="estoque-title">Estoque Connect - prontos para ver agora</h1>
          <p>
            Aqui voce agenda para ver os carros que estao fisicamente conosco em Osasco. Visitou, gostou,
            fechou: cuidamos de toda a burocracia enquanto voce aproveita a experiencia premium.
          </p>
          <div className="page-hero__actions">
            <a
              className="page-button page-button--primary"
              href={WHATSAPP_LINKS.estoque}
              target="_blank"
              rel="noreferrer"
            >
              Agendar visita
            </a>
          </div>
        </div>
      </section>

      <section className="section-card" aria-labelledby="estoque-grid">
        <h2 id="estoque-grid">Carros no estoque proprio Connect</h2>
        <p className="section-helper">
          Escolha um modelo para ver agora em nossa vaga exclusiva. Os que aparecem aqui ja estao prontos
          para test-drive e fechamento imediato.
        </p>

        {loading && <p>Carregando estoque...</p>}

        {!loading && visibleVehicles.length === 0 && (
          <div className="empty-state">
            Nenhum veiculo disponivel no momento. Fale com nosso time para ser avisado assim que uma nova
            oportunidade chegar.
          </div>
        )}

        {!loading && visibleVehicles.length > 0 && (
          <div className="grid">
            {visibleVehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        )}

        {!loading && usingFallback && visibleVehicles.length > 0 && (
          <div className="helper-text">
            Exibindo todos os veiculos ativos porque nenhum foi marcado como estoque proprio. Atualize a
            classificacao no painel administrativo para separar estoque e locadora.
          </div>
        )}
      </section>

      <section className="section-card" aria-labelledby="estoque-chamada">
        <h2 id="estoque-chamada">Quer comparar com as ofertas de locadora?</h2>
        <p>
          Nosso time tambem acompanha voce no patio da locadora com condicoes abaixo da FIPE. Veja a secao
          <strong> Estoque de Locadora</strong> ou fale direto no WhatsApp para organizar uma agenda com as duas opcoes.
        </p>
        <div className="page-hero__actions">
          <a
            className="page-button page-button--ghost"
            href={WHATSAPP_LINKS.locadora}
            target="_blank"
            rel="noreferrer"
          >
            Ver carros de locadora
          </a>
        </div>
      </section>
    </div>
  );
}
